export function getAbsolutePosition(elem: HTMLElement) {
    let left = 0;
    let top = 0;

    do {
        left += elem.offsetLeft - elem.scrollLeft;
        top += elem.offsetTop - elem.scrollTop;
        // eslint-disable-next-line no-cond-assign, no-param-reassign
    } while (elem = (elem.offsetParent as HTMLElement || elem.parentElement as HTMLElement));

    return { x: left, y: top };
}

/**
 * Computes an element's position relative to its nearest *positioned* ancestor
 * (`relative`/`absolute`/`fixed`/`sticky` - all of these establish a containing block
 * for `position: absolute` descendants per spec), matching the containing block a
 * `position: absolute` popup rendered nearby would resolve against.
 *
 * Unlike `getAbsolutePosition`, this is safe when that ancestor is `position: sticky`:
 * sticky elements report their static (unstuck) `offsetTop`/`offsetLeft` rather than
 * their true rendered position, which throws off any offsetParent-chain summation that
 * passes through them. Since `getBoundingClientRect()` always reflects true rendered
 * position, comparing two rects directly sidesteps that quirk entirely (and cancels out
 * scroll for free) while still stopping at the correct (sticky) containing block.
 *
 * Falls back to page-relative coordinates if no ancestor is positioned.
 */
export function getPositionRelativeToPositionedAncestor(elem: HTMLElement) {
    let ancestor = elem.parentElement;
    while (ancestor) {
        const { position } = getComputedStyle(ancestor);
        if (position === 'relative' || position === 'absolute' || position === 'fixed' || position === 'sticky') break;
        ancestor = ancestor.parentElement;
    }

    const elemRect = elem.getBoundingClientRect();
    const ancestorRect = ancestor ? ancestor.getBoundingClientRect() : { left: -window.scrollX, top: -window.scrollY };

    return { x: elemRect.left - ancestorRect.left, y: elemRect.top - ancestorRect.top };
}
